import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import {
    MdLocalFireDepartment,
    MdListAlt,
    MdCalendarToday,
    MdExpandMore,
    MdRestaurant,
} from "react-icons/md";
import {
    GiMuscleUp,
    GiWheat,
    GiButter,
    GiMeal
} from "react-icons/gi";
import { loadStripe } from '@stripe/stripe-js';
import { useLocation } from "react-router-dom";
import isProd from '../config';
// const isProd = true;
import ReactGA from "react-ga4";
import FullScreenLoader from "./FullScreenLoader";

const API_BASE_URL = isProd
    ? process.env.REACT_APP_API_BASE_URL_LIVE
    : process.env.REACT_APP_API_BASE_URL_TEST;

const REACT_APP_API_URL_LIVE = isProd
    ? process.env.REACT_APP_API_URL_LIVE
    : process.env.REACT_APP_API_URL_TEST;

const stripePromise = loadStripe(
    isProd
        ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE
        : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST
);

// Enhanced CollapsibleSection component with Tailwind
function CollapsibleSection({ title, icon, children, open, onClick, id, labelId }) {
    const contentRef = useRef(null);
    const [height, setHeight] = useState(open ? "auto" : "0px");

    useEffect(() => {
        if (open) {
            const scrollHeight = contentRef.current?.scrollHeight || 0;
            setHeight(scrollHeight + "px");
            const timeout = setTimeout(() => setHeight("auto"), 300);
            return () => clearTimeout(timeout);
        } else {
            if (contentRef.current) {
                setHeight(contentRef.current.scrollHeight + "px");
                setTimeout(() => setHeight("0px"), 10);
            }
        }
    }, [open, children]);

    return (
        <section
            className="bg-white rounded-xl shadow-lg mb-6 border-2 border-gray-200 hover:shadow-xl transition-shadow duration-200"
            aria-labelledby={labelId}
        >
            <button
                className="w-full flex items-center justify-between px-6 py-5 focus:outline-none text-xl md:text-2xl font-bold text-green-800 bg-green-50 hover:bg-green-100 rounded-t-xl border-none cursor-pointer transition-colors duration-200 min-h-[48px] focus:ring-4 focus:ring-yellow-300"
                onClick={onClick}
                aria-expanded={open}
                aria-controls={id}
                id={labelId}
                type="button"
            >
                <span className="flex items-center gap-3">
                    {icon}
                    {title}
                </span>
                <MdExpandMore
                    size={48}
                    className={`transition-transform duration-300 ${open ? "rotate-180" : ""} text-green-600`}
                />
            </button>
            <div
                id={id}
                ref={contentRef}
                className="overflow-hidden transition-all duration-300 bg-green-50"
                style={{ maxHeight: height }}
            >
                <div className="px-6 py-4 text-lg md:text-xl leading-relaxed">
                    {children}
                </div>
            </div>
        </section>
    );
}


const mealOrder = [
    "breakfast",
    "lunch",
    "dinner",
    "bedtime_snack",
    "snack",
    "snack_1",
    "snack1",
    "snack_2",
    "snack2",
];

// Updated WhatsIncluded component with prioritized benefits and evidence-based meal planning
export function WhatsIncluded() {
    const weeklyBenefits = [
        {
            icon: "📅",
            title: "Fresh Weekly Meal Plans",
            description: "New recipes every Monday designed for your current treatment phase",
            highlight: "4-5 plans per month"
        },
        {
            icon: "🔄",
            title: "Monthly Plan Regeneration",
            description: "Retake the assessment monthly to update your nutrition plan",
            highlight: "Always current"
        },
        {
            icon: "👨‍⚕️",
            title: "Oncology-Expert Designed",
            description: "Every recipe vetted by cancer nutrition specialists",
            highlight: "Medically safe"
        },
        {
            icon: "🛡️",
            title: "Treatment-Safe Ingredients",
            description: "Pre-screened to avoid drug interactions",
            highlight: "Verified safe"
        },
        {
            icon: "📋",
            title: "Easy Grocery Lists",
            description: "Organized shopping lists for each week's meals",
            highlight: "Caregiver friendly"
        },
        {
            icon: "⚡",
            title: "Energy-Conscious Recipes",
            description: "Simple instructions for low-energy days",
            highlight: "15-30 min prep"
        }
    ];


    return (
        <section className="bg-white border-2 border-gray-200 rounded-2xl shadow-lg p-8 mt-8 mb-6">
            <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                    What You Get Every Week & Month
                </h3>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6">
                    <div className="text-lg font-semibold text-blue-800 mb-2">
                        🔄 Smart Weekly Delivery + Monthly Updates
                    </div>
                    <p className="text-blue-700">
                        <strong>Every Monday:</strong> Fresh meal plans adapted to your needs<br />
                        <strong>Every Month:</strong> Retake quiz for updated nutrition plans based on symptom changes
                    </p>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {weeklyBenefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="text-2xl">{benefit.icon}</div>
                        <div className="flex-1">
                            <h4 className="font-bold text-gray-800 mb-1">{benefit.title}</h4>
                            <p className="text-gray-600 text-md mb-2">{benefit.description}</p>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                                {benefit.highlight}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Value proposition */}
            <div className="mt-10 px-6 py-8 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-2xl shadow-lg max-w-3xl mx-auto">
                <h4 className="font-extrabold text-2xl mb-6 text-green-900 text-center tracking-tight">
                    Exceptional Value for Your Health Journey
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                    {/* Recipes per month */}
                    <div className="flex flex-col items-center">
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 mb-3 shadow-sm">
                            <span className="text-3xl">🍲</span>
                        </div>
                        <div className="text-3xl font-extrabold text-green-800 mb-1">135+</div>
                        <div className="text-base text-green-700 font-medium">New recipes per month</div>
                    </div>
                    {/* Cost per day */}
                    <div className="flex flex-col items-center">
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-blue-100 mb-3 shadow-sm">
                            <span className="text-3xl">💸</span>
                        </div>
                        <div className="text-3xl font-extrabold text-blue-800 mb-1">$0.33</div>
                        <div className="text-base text-blue-700 font-medium">Per day for complete nutrition</div>
                    </div>
                    {/* 24/7 Access */}
                    <div className="flex flex-col items-center">
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-yellow-100 mb-3 shadow-sm">
                            <span className="text-3xl">⏰</span>
                        </div>
                        <div className="text-3xl font-extrabold text-yellow-800 mb-1">24/7</div>
                        <div className="text-base text-yellow-700 font-medium">Access to your meal plans</div>
                    </div>
                </div>
            </div>

        </section>
    );
}

function formatGroceryItem(item) {
    if (!item) return "";
    const qty = item.quantity !== undefined ? item.quantity : "";
    const unit = item.unit ? item.unit : "";
    const ingredient = item.ingredient ? item.ingredient : "";
    return `${qty} ${unit} ${ingredient}`.replace(/\s+/g, " ").trim();
}


export default function PlanPage() {
    const location = useLocation();
    const email = location.state?.email;
    const { planId } = useParams();
    const cleanPlanId = planId.split('&')[0];
    const [loadingbutton, setLoadingButton] = useState(false);
    const [showAgreementError, setShowAgreementError] = useState(false);

    const [plan, setPlan] = useState(null);
    const [error, setError] = useState("");
    const [groceryOpen, setGroceryOpen] = useState(false);
    const [openDays, setOpenDays] = useState([]);
    const [recommendationsOpen, setRecommendationsOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    // const [loadingPaypal, setLoadingPaypal] = useState(false);
    const [subscriptionAgreed, setSubscriptionAgreed] = useState(false);
    const pricingRef = useRef(null);
    const [showBottomBar, setShowBottomBar] = useState(false);
    const getQueryParam = (param) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    };

    useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
    }, []);

    const getSource = () => {
        // Check if source exists in URL
        let source = getQueryParam("source");
        // If not in URL, check referrer
        if (!source && document.referrer) {
            source = new URL(document.referrer).hostname;
        }
        // If still no source, default to "direct"
        return source || "direct";
    };

    const getUTM = () => {
        let utm = getQueryParam("utm_content");
        if (!utm && document.referrer) {
            utm = new URL(document.referrer).hostname;
        }
        return utm || "empty";
    };

    const getAdname = () => {
        const adName = getQueryParam("ad_name");
        return adName || "empty";
    };

    const getAdId = () => {
        const adId = getQueryParam("ad_id");
        return adId || "empty";
    };

    useEffect(() => {
        function onScroll() {
            if (!pricingRef.current) return;
            const rect = pricingRef.current.getBoundingClientRect();
            setShowBottomBar(rect.bottom < 0);
        }
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const createCheckoutSession = async (productType) => {

        try {
            const stripe = await stripePromise;
            const source = getSource();
            const utm = getUTM();
            const ad_name = getAdname();
            const ad_id = getAdId();

            const payload = {
                email,
                source,
                ad_id,
                utm,
                ad_name,
                planId: planId,
            };
            const response = await fetch(`${API_BASE_URL}/create-checkout-session-diet`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Failed to create checkout session');
            }

            const { id: sessionId } = await response.json();

            const { error } = await stripe.redirectToCheckout({ sessionId });
            if (error) throw error;
        } catch (error) {
            console.error('Checkout error:', error);
        }
    };



    function handleSubscribe() {
        if (!subscriptionAgreed) {
            // alert("Please confirm that you understand this is a monthly subscription before proceeding.");
            setShowAgreementError(true);
            return;
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'PURCHASE_INITIATED',
            value: 9.99,
            currency: 'USD',
            items: [
                {
                    item_id: 'monthly_meal_plan',
                    item_name: 'Monthly Meal Plan',
                    price: 9.99,
                    quantity: 1,
                    item_category: 'Subscription',
                    plan_id: cleanPlanId,
                }
            ],
            plan_id: cleanPlanId,
            subscription_type: 'monthly',
            plan_name: 'Monthly Meal Plan'
        });
        // setSubscribed(true);
        // setLoadingPaypal(true);
        setShowAgreementError(false);
        setLoadingButton(true)
        createCheckoutSession('diet');
        // localStorage.setItem("subscribed", "true");
    }

    const scrollToPricing = () => {
        if (pricingRef.current) {
            pricingRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    function removeCitationBrackets(text) {
        if (!text || typeof text !== "string") return text;
        return text.replace(/\[\d+\]/g, "");
    }

    function sortMealsByType(mealsObj) {
        const mealsArr = Object.entries(mealsObj).map(([key, meal]) => ({
            ...meal,
            _mealKey: key
        }));
        return mealsArr.sort((a, b) => {
            const typeA = (a.meal_type || a._mealKey || "").toLowerCase();
            const typeB = (b.meal_type || b._mealKey || "").toLowerCase();
            const idxA = mealOrder.indexOf(typeA);
            const idxB = mealOrder.indexOf(typeB);
            return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
        });
    }


    useEffect(() => {
        setLoading(true);
        fetch(`${REACT_APP_API_URL_LIVE}/?planId=${planId}`, {
            method: 'POST'
        })
            .then(res => {
                if (!res.ok) throw new Error("Plan not found.");
                return res.json();
            })
            .then(data => {
                setPlan(data);
                if (data?.meal_plan?.days?.length > 0) setOpenDays([0]);
            })
            .catch(err => setError(err.message || "Error loading plan."))
            .finally(() => setLoading(false));
    }, [planId]);

    if (loading)
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 px-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-600 border-t-transparent mx-auto mb-4"></div>
                    <span className="text-2xl md:text-3xl text-green-700 font-bold">Creating your personalized meal plan...</span>
                </div>
            </div>
        );

    if (error)
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 px-4">
                <div className="text-center max-w-md">
                    <span className="text-xl md:text-2xl font-semibold text-red-700 mb-4 block">
                        We're sorry, but we couldn't load your meal plan. Please try again or contact support.
                    </span>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg text-lg transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-yellow-300 min-h-[48px]"
                        aria-label="Try loading the meal plan again"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );

    if (!plan) return null;

    const recommendationsArr = plan.recommendations?.recommendations || [];
    const days = plan.meal_plan?.days || [];
    const mealsPerDay = plan?.recommendations?.meals_per_day;
    const calorieTarget = plan?.recommendations?.calorie_target;
    const recommendedIngredients = plan?.recommendations?.recommended_ingredients || [];
    const avoidIngredients = plan?.recommendations?.avoid_ingredients || [];

    const toggleDay = idx =>
        setOpenDays(openDays =>
            openDays.includes(idx)
                ? openDays.filter(i => i !== idx)
                : [...openDays, idx]
        );


    return (
        <div className="min-h-screen bg-amber-50 flex flex-col items-center py-4 px-4">
            {loadingbutton && <FullScreenLoader />}

            {/* Print Button Only */}
            {/* <div className="w-full max-w-4xl flex justify-end mb-6">
                <button
                    onClick={() => window.print()}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg text-lg shadow-lg transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-yellow-300 min-h-[48px] flex items-center gap-2"
                    aria-label="Print meal plan as PDF"
                >
                    <MdPrint size={24} />
                    Print Plan
                </button>
            </div> */}

            {/* Print Styles */}
            <style>{`
                @media print {
                    button[aria-label="Print meal plan as PDF"] {
                        display: none !important;
                    }
                    .shadow, .shadow-lg, .shadow-xl {
                        box-shadow: none !important;
                    }
                    body, .bg-amber-50 {
                        background: #fff !important;
                    }
                    [style*="max-height"] {
                        max-height: none !important;
                        overflow: visible !important;
                    }
                }
            `}</style>

            {/* Header */}
            <header className="w-full max-w-4xl text-center mb-8">
                <div className="flex items-center justify-center gap-4 mb-4">
                    <img src="/logo.svg" alt="Logo" width={40} height={40} />
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-green-800 leading-tight">
                        Your Personalized Meal Plan
                        <span className="text-green-600 ml-4 text-2xl md:text-3xl">
                            {/* ({numDays} day{numDays > 1 ? "s" : ""}) */}
                        </span>
                    </h1>
                </div>
                <p className="text-green-700 text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed">
                    Nourishing your health — during treatment, through recovery, and for lifelong well-being.
                    Enjoy delicious meals designed to help you eat healthier at every stage.
                </p>
            </header>




            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl shadow-xl p-8 max-w-4xl mx-auto mb-8 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>

                {/* <div className="relative z-10"> */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center bg-blue-100 text-blue-800 px-6 py-3 rounded-full text-base font-bold mb-6">
                        <span className="mr-2">🔄</span>
                        Weekly Fresh Plans + Monthly Personal Updates
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800">
                        Your Complete Cancer Care Nutrition Plan
                    </h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Get personalized weekly meal plans plus monthly plan regeneration based on your changing needs and symptoms
                    </p>
                </div>

                {/* Value Proposition Cards */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl p-6 shadow-md border border-blue-100">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">📅</span>
                            </div>
                            <h3 className="font-bold text-lg mb-2">Fresh Weekly Plans</h3>
                            <p className="text-md text-gray-600">
                                New meal plans delivered weekly, designed specifically for cancer patients
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-md border border-blue-100">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">🔄</span>
                            </div>
                            <h3 className="font-bold text-lg mb-2">Monthly Regeneration</h3>
                            <p className="text-md text-gray-600">
                                Retake the quiz monthly to update plans based on new symptoms and condition changes
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl p-6 shadow-md border border-blue-100">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">🎯</span>
                            </div>
                            <h3 className="font-bold text-lg mb-2">Always Relevant</h3>
                            <p className="text-md text-gray-600">
                                Plans adapt to your current treatment phase, side effects, and energy levels
                            </p>
                        </div>
                    </div>
                </div>




                {/* </div> */}
            </div>

            {/* Pricing Section */}
            <>
                <div ref={pricingRef} className="max-w-4xl mx-auto p-6 bg-white rounded-2xl shadow-lg">
                    {/* Header Section - Clear and Focused */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                            <span className="mr-2">✨</span>
                            Complete Cancer Care Nutrition
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-3 text-gray-900">
                            Get Your Personalized Meal Plan
                        </h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Weekly personalized meal plans crafted for your unique health needs, updated monthly as your journey evolves                            </p>
                    </div>

                    {/* Pricing Card - Simplified and Focused */}
                    <div className="bg-white rounded-2xl shadow-2xl border-2 border-blue-200 max-w-md mx-auto p-0 overflow-hidden mb-6">
                        {/* Top Blue Gradient Banner */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 px-8 pt-8 pb-6 border-b border-blue-100">
                            <div className="flex items-end justify-center gap-4 mb-4">
                                <span className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-none">$9.99</span>
                                <div className="flex flex-col items-start">
                                    <span className="text-xl text-gray-500 mb-1">/month</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg text-gray-400 line-through">$14.99</span>
                                        <span className="text-xs font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Save 33%</span>
                                    </div>
                                </div>
                            </div>

                            {/* Value Proposition Cards */}
                            <div className="grid grid-cols-3 gap-3 mt-4">
                                <div className="flex flex-col items-center bg-white rounded-lg shadow-sm py-4">
                                    <div className="text-2xl font-extrabold text-green-600 mb-1">4–6</div>
                                    <div className="text-xs text-gray-500 text-center">Fresh meals daily</div>
                                </div>
                                <div className="flex flex-col items-center bg-white rounded-lg shadow-sm py-4">
                                    <div className="text-2xl font-extrabold text-purple-600 mb-1">135+</div>
                                    <div className="text-xs text-gray-500 text-center">Recipes monthly</div>
                                </div>
                                <div className="flex flex-col items-center bg-white rounded-lg shadow-sm py-4">
                                    <div className="text-2xl font-extrabold text-yellow-600 mb-1">$0.07</div>
                                    <div className="text-xs text-gray-500 text-center">Per recipe</div>
                                </div>
                            </div>
                        </div>

                        {/* Agreement Section */}
                        <div className="px-8 py-6 bg-white border-b border-gray-100">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={subscriptionAgreed}
                                    onChange={(e) => setSubscriptionAgreed(e.target.checked)}
                                    className={`w-5 h-5 rounded border-2 focus:ring-2 focus:ring-green-400 transition-colors ${showAgreementError
                                        ? "accent-gray-600 border-gray-600"
                                        : "accent-green-500 border-gray-300"
                                        }`}
                                />
                                <div className={`text-base font-semibold mb-1 ${showAgreementError ? "text-red-600" : "text-gray-800"
                                    }`}>
                                    <span>I agree to the </span>
                                    <Link target="_blank" to="/terms" className="underline hover:text-[#4B8F5A] transition">
                                        Terms & Conditions
                                    </Link>
                                    <span> and </span>
                                    <Link target="_blank" to="/privacy" className="underline hover:text-[#4B8F5A] transition">
                                        Privacy Policy
                                    </Link>
                                </div>
                            </label>
                            {showAgreementError && (
                                <p className="text-red-600 text-sm mt-1">
                                    You must agree to the Terms & Conditions and Privacy Policy to continue.
                                </p>
                            )}

                        </div>

                        {/* CTA Buttons */}
                        <div className="px-8 py-6 bg-white">
                            <button
                                onClick={handleSubscribe}
                                className={`w-full py-4 px-6 rounded-xl text-lg font-bold transition-all duration-200 mb-3 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white shadow-lg"
                                    
                                    }`}
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                                        Processing...
                                    </span>
                                ) : (
                                    "Start My 7-Day Free Trial"
                                )}
                            </button>

                            <button
                                onClick={handleSubscribe}
                                // disabled={loadingPaypal || !subscriptionAgreed}
                                className={`w-full py-4 px-6 rounded-xl text-lg font-bold border-2 transition-all duration-200 flex items-center justify-center border-yellow-400 bg-yellow-50 text-yellow-800 hover:bg-yellow-100"
                                   
                                    }`}
                            >

                                <span className="flex items-center gap-2">
                                    <span>Pay with</span>
                                    <span className="font-bold">PayPal</span>
                                </span>
                            </button>
                        </div>
                    </div>


                    {/* Trust Signals - Better Positioned */}
                    <div className="text-center">
                        <div className="flex flex-col items-center gap-4">
                            <div className="flex flex-col items-center gap-2 text-md text-gray-600">
                                <span className="flex items-center gap-1">
                                    <i className="fab fa-cc-visa w-10 h-6"></i>
                                    <i className="fab fa-cc-mastercard w-10 h-6"></i>
                                    <i className="fab fa-cc-amex w-10 h-6"></i>
                                    <i className="fab fa-cc-paypal w-10 h-6"></i>
                                    <i className="fab fa-google-pay w-10 h-6"></i>
                                    <i className="fab fa-apple w-10 h-6"></i>
                                </span>
                            </div>
                            <div className="flex items-center justify-center gap-6 text-sm text-gray-600">

                                <div className="flex items-center gap-2">
                                    <span className="text-green-600">🔒</span>
                                    <span>SSL Encrypted</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-blue-600">💳</span>
                                    <span>Secure Payment</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-purple-600">⚡</span>
                                    <span>Powered by Stripe</span>
                                </div>
                            </div>

                            <div className="text-xs text-gray-500 max-w-md">
                                By starting your trial, you agree to automatic billing at $9.99/month after 7 days.
                                Cancel anytime by emailing support@oncodiary.com
                            </div>
                        </div>
                    </div>
                </div>


                {/* Enhanced WhatsIncluded */}
                <WhatsIncluded />
            </>

            {/* Main Content */}
            <main className="w-full max-w-4xl" role="main">
                {/* Recommendations Section */}
                <CollapsibleSection
                    title={
                        <div className="flex items-center gap-2">
                            Nutrition Recommendations
                            {/* <HelpTooltip
                                text="These recommendations are based on your specific health needs and treatment plan"
                                ariaLabel="Help about nutrition recommendations"
                            /> */}
                        </div>
                    }
                    icon={<MdListAlt size={36} aria-label="Recommendations" />}
                    open={recommendationsOpen}
                    onClick={() => setRecommendationsOpen(open => !open)}
                    id="recommendations-section"
                    labelId="recommendations-label"
                >
                    {(mealsPerDay || calorieTarget) && (
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
                            <h3 className="font-bold text-xl mb-4 text-blue-800">Your Daily Targets:</h3>
                            <div className="flex flex-wrap gap-6">
                                {mealsPerDay && (
                                    <div className="flex items-center gap-2 text-lg">
                                        <MdRestaurant size={28} className="text-blue-600" />
                                        <span><strong>Meals per day:</strong> {mealsPerDay}</span>
                                    </div>
                                )}
                                {calorieTarget && (
                                    <div className="flex items-center gap-2 text-lg">
                                        <MdLocalFireDepartment size={28} className="text-orange-500" />
                                        <span><strong>Daily calories:</strong> {calorieTarget}</span>
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 p-3 bg-white rounded border border-blue-300">
                                <p className="text-sm text-blue-700">
                                    <strong>Note:</strong> Our meal plans follow evidence-based guidelines recommending
                                    <strong> 5-6 eating occasions daily</strong> (3 main meals + 2-3 snacks) spaced every 2-3 hours
                                    for optimal nutrition during cancer treatment.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-6 relative">
                        {/* First two recommendations: fully visible */}
                        {recommendationsArr.slice(0, 2).map((rec, idx) => (
                            <div
                                key={idx}
                                className="border-l-4 border-green-400 pl-6 py-2"
                            >
                                <h4 className="font-bold text-xl mb-3 text-green-800">{rec.recommendation}</h4>
                                <p className="text-gray-700 text-lg leading-relaxed">{removeCitationBrackets(rec.rationale)}</p>
                            </div>
                        ))}

                        {/* Blurred block for the rest */}
                        {recommendationsArr.length > 2 && (
                            <div className="relative">
                                <div className="space-y-6 filter blur-sm pointer-events-none select-none opacity-100">
                                    {recommendationsArr.slice(2).map((rec, idx) => (
                                        <div
                                            key={idx + 2}
                                            className="border-l-4 border-green-400 pl-6 py-2"
                                        >
                                            <h4 className="font-bold text-xl mb-3 text-green-800">{rec.recommendation}</h4>
                                            <p className="text-gray-700 text-lg leading-relaxed">{removeCitationBrackets(rec.rationale)}</p>
                                        </div>
                                    ))}
                                </div>
                                {/* Overlay shown ONCE for all blurred items */}
                                <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded pointer-events-auto">
                                    <button
                                        onClick={scrollToPricing}
                                        className="px-6 py-3 rounded-lg bg-gradient-to-r from-green-400 to-blue-500 text-white font-bold text-lg shadow-lg hover:from-green-500 hover:to-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        aria-label="Start Free Trial to unlock and scroll to pricing"
                                    >
                                        Start Free Trial
                                    </button>
                                </div>


                            </div>
                        )}
                    </div>


                    {recommendedIngredients.length > 0 && (
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mt-6 relative">
                            <h3 className="font-bold text-xl mb-4 flex items-center gap-2 text-green-800">
                                <GiWheat size={28} />
                                Recommended Ingredients:
                            </h3>
                            <ul className="space-y-3">
                                {/* Show first two items fully */}
                                {recommendedIngredients.slice(0, 2).map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <span className="text-green-600 mt-1 text-xl">•</span>
                                        <div className="text-lg">
                                            <strong className="text-green-800">{item.ingredient}</strong>
                                            {item.rationale && (
                                                <span className="text-gray-700 ml-2 block mt-1">— {item.rationale}</span>
                                            )}
                                        </div>
                                    </li>
                                ))}

                                {/* Blurred items as normal <li>s */}
                                {recommendedIngredients.length > 2 && (
                                    <li className="relative p-0 m-0 list-none">
                                        {/* Blurred list */}
                                        <ul className="space-y-3 filter blur-sm opacity-60 pointer-events-none select-none m-0">
                                            {recommendedIngredients.slice(2).map((item, idx) => (
                                                <li key={idx + 2} className="flex items-start gap-3">
                                                    <span className="text-green-600 mt-1 text-xl">•</span>
                                                    <div className="text-lg">
                                                        <strong className="text-green-800">{item.ingredient}</strong>
                                                        {item.rationale && (
                                                            <span className="text-gray-700 ml-2 block mt-1">— {item.rationale}</span>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                        {/* Overlay shown once */}
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded pointer-events-auto">
                                            <button
                                                onClick={scrollToPricing}
                                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-green-400 to-blue-500 text-white font-bold text-lg shadow-lg hover:from-green-500 hover:to-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                aria-label="Start Free Trial to unlock and scroll to pricing"
                                            >
                                                Start Free Trial
                                            </button>
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}




                    {avoidIngredients.length > 0 && (
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mt-6 relative">
                            <h3 className="font-bold text-xl mb-4 flex items-center gap-2 text-red-800">
                                <MdListAlt size={28} />
                                Ingredients to Avoid:
                            </h3>
                            <ul className="space-y-3">
                                {/* First two items fully visible */}
                                {avoidIngredients.slice(0, 2).map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <span className="text-red-600 mt-1 text-xl">•</span>
                                        <div className="text-lg">
                                            <strong className="text-red-800">{item.ingredient}</strong>
                                            {item.rationale && (
                                                <span className="text-red-700 ml-2 block mt-1">— {item.rationale}</span>
                                            )}
                                        </div>
                                    </li>
                                ))}

                                {/* Blurred block for the rest */}
                                {avoidIngredients.length > 2 && (
                                    <li className="relative p-0 m-0 list-none">
                                        <ul className="space-y-3 filter blur-sm opacity-60 pointer-events-none select-none m-0">
                                            {avoidIngredients.slice(2).map((item, idx) => (
                                                <li key={idx + 2} className="flex items-start gap-3">
                                                    <span className="text-red-600 mt-1 text-xl">•</span>
                                                    <div className="text-lg">
                                                        <strong className="text-red-800">{item.ingredient}</strong>
                                                        {item.rationale && (
                                                            <span className="text-red-700 ml-2 block mt-1">— {item.rationale}</span>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                        {/* Overlay shown once for all blurred items */}
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded pointer-events-auto">
                                            <button
                                                onClick={scrollToPricing}
                                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-green-400 to-blue-500 text-white font-bold text-lg shadow-lg hover:from-green-500 hover:to-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                aria-label="Start Free Trial to unlock and scroll to pricing"
                                            >
                                                Start Free Trial
                                            </button>
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}


                </CollapsibleSection>


                <div className="my-16 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">

                    <h3 className="max-w-xl text-3xl font-bold leading-tight text-gray-900 m-0">
                        Preview an 1 day of your plan. <br className="hidden sm:block" />
                    </h3>
                    <h2 className="m-0">
                        <span className="text-blue-600 text-2xl">Start a free trial</span>
                        <span className="text-2xl"> to unlock all days and recipes.</span>
                    </h2>


                    <button
                        onClick={scrollToPricing}
                        className="inline-flex items-center justify-center px-7 py-3 rounded-md
               bg-gradient-to-r from-green-500 to-blue-600 text-white text-lg font-semibold
               shadow hover:from-green-600 hover:to-blue-700 focus:outline-none
               focus-visible:ring-2 focus-visible:ring-blue-400 active:scale-95 transition"
                        aria-label="Start free trial and scroll to pricing"
                    >
                        Start Free Trial
                    </button>
                </div>



                {days.map((day, dayIdx) => {
                    const isUnlocked = dayIdx === 0;
                    const isPreview = !isUnlocked;

                    return (
                        <CollapsibleSection
                            key={day.day}
                            title={day.day}
                            icon={<MdCalendarToday size={36} aria-label="Day" />}
                            open={openDays.includes(dayIdx)}
                            onClick={() => toggleDay(dayIdx)}
                            id={`day-section-${dayIdx}`}
                            labelId={`day-label-${dayIdx}`}
                        >
                            <div className="relative">
                                {/* Overlay for locked days (2nd, 3rd, ...) */}
                                {!isUnlocked && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded pointer-events-auto z-10">
                                        <button
                                            onClick={scrollToPricing}
                                            className="px-6 py-3 rounded-lg bg-gradient-to-r from-green-400 to-blue-500 text-white font-bold text-lg shadow-lg hover:from-green-500 hover:to-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            aria-label="Start Free Trial to unlock and scroll to pricing"
                                        >
                                            Start Free Trial
                                        </button>
                                    </div>
                                )}

                                {/* Meal content (always rendered, but interaction blocked by overlay if locked) */}
                                <div
                                    className={
                                        isPreview
                                            ? "max-h-[400px] overflow-hidden pointer-events-none select-none opacity-10"
                                            : ""
                                    }
                                >                                    <div className="space-y-8">
                                        {sortMealsByType(day.meals).map((meal, mealIdx) => {
                                            const hasImage = !!meal.imageUrl;
                                            return (
                                                <article
                                                    key={mealIdx}
                                                    className="bg-white rounded-xl shadow-lg p-0 border-2 border-gray-100 hover:shadow-xl transition-shadow duration-200 overflow-hidden"
                                                    aria-label={`${meal.meal_type} - ${meal.name}`}
                                                >
                                                    <div className={`flex flex-col${hasImage ? " md:flex-row" : ""}`}>
                                                        <div className={`w-full${hasImage ? " md:w-1/2" : ""} p-6 flex flex-col justify-between`}>
                                                            <div>
                                                                <div className="flex items-center gap-3 mb-3">
                                                                    <GiMeal size={28} className="text-green-700" aria-label="Meal" />
                                                                    <h3 className="text-xl font-bold text-green-800">
                                                                        {meal.meal_type
                                                                            ? meal.meal_type.charAt(0).toUpperCase() + meal.meal_type.slice(1)
                                                                            : "Meal"}
                                                                        <span className="text-green-600 font-normal"> • </span>
                                                                        <span className="text-gray-700">{meal.name}</span>
                                                                    </h3>
                                                                </div>
                                                                {/* Mobile: image below name */}
                                                                {hasImage && (
                                                                    <div className="block md:hidden w-full flex justify-center mb-4">
                                                                        <div className="w-full h-48">
                                                                            <img
                                                                                src={meal.imageUrl}
                                                                                alt={meal.name || "Meal image"}
                                                                                className="w-full h-full object-cover rounded-lg shadow-sm"
                                                                                onError={e => (e.target.style.display = 'none')}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                <div className="flex flex-wrap gap-4 text-md text-gray-600 mb-4">
                                                                    {meal.preparation_time !== undefined && (
                                                                        <span><strong>Prep:</strong> {meal.preparation_time} min</span>
                                                                    )}
                                                                    {meal.cook_time !== undefined && (
                                                                        <span><strong>Cook:</strong> {meal.cook_time} min</span>
                                                                    )}
                                                                    <span>
                                                                        <strong>Total:</strong>{" "}
                                                                        {meal.total_time !== undefined
                                                                            ? `${meal.total_time} min`
                                                                            : `${(meal.preparation_time || 0) + (meal.cook_time || 0)} min`}
                                                                    </span>
                                                                </div>
                                                                <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-6">
                                                                    <div className="flex items-center gap-2">
                                                                        <MdLocalFireDepartment size={20} className="text-orange-500" />
                                                                        <span className="text-sm"><strong className="text-orange-600">Calories:</strong> {meal.calories}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <GiMuscleUp size={20} className="text-blue-600" />
                                                                        <span className="text-sm"><strong className="text-blue-600">Protein:</strong> {meal.nutrition?.protein}g</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <GiWheat size={20} className="text-green-600" />
                                                                        <span className="text-sm"><strong className="text-green-600">Carbs:</strong> {meal.nutrition?.carbs}g</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <GiButter size={20} className="text-yellow-600" />
                                                                        <span className="text-sm"><strong className="text-yellow-600">Fat:</strong> {meal.nutrition?.fat}g</span>
                                                                    </div>
                                                                </div>
                                                                <div className="mb-6">
                                                                    <h4 className="font-semibold text-lg mb-3 text-gray-800">Ingredients:</h4>
                                                                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                                                        {Array.isArray(meal.ingredients) &&
                                                                            meal.ingredients.map((ing, i) => (
                                                                                <li key={i} className="flex items-start gap-2 text-sm">
                                                                                    <span className="text-green-600 mt-1 text-xs">•</span>
                                                                                    <span className="text-gray-700">{ing}</span>
                                                                                </li>
                                                                            ))}
                                                                    </ul>
                                                                </div>
                                                                <div>
                                                                    <h4 className="font-semibold text-lg mb-3 text-gray-800">Instructions:</h4>
                                                                    <ol className="space-y-2 text-md">
                                                                        {(meal.preparation || "")
                                                                            .split(/(?=Step \d+:)/g)
                                                                            .filter(Boolean)
                                                                            .map((step, i, arr) => {
                                                                                const isLastNote = i === arr.length - 1 && !/^Step \d+:/.test(step.trim());
                                                                                return (
                                                                                    <li key={i} className={`flex gap-3 leading-relaxed ${isLastNote ? "italic font-medium text-green-700" : "text-gray-700"}`}>
                                                                                        {!isLastNote && (
                                                                                            <span className="bg-green-100 text-green-700 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
                                                                                                {i + 1}
                                                                                            </span>
                                                                                        )}
                                                                                        <span className={!isLastNote ? "" : "ml-9"}>
                                                                                            {step.trim().replace(/^Step \d+:\s*/, "")}
                                                                                        </span>
                                                                                    </li>
                                                                                );
                                                                            })}
                                                                    </ol>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/* Desktop: image on right, 50% width. If no image, this is hidden. */}
                                                        {hasImage && (
                                                            <div className="hidden md:flex w-full md:w-1/2 items-center justify-center bg-gray-50">
                                                                <div className="w-full h-full flex items-center justify-center p-8">
                                                                    <img
                                                                        src={meal.imageUrl}
                                                                        alt={meal.name || "Meal image"}
                                                                        className="w-full h-[20rem] max-h-[28rem] object-cover rounded-lg shadow-md"
                                                                        onError={e => (e.target.style.display = 'none')}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </CollapsibleSection>
                    );
                })}




                {/* Grocery List Section */}

                {Array.isArray(plan.grocery_list) && plan.grocery_list.length > 0 && (
                    <CollapsibleSection
                        title="Shopping List"
                        icon={<MdListAlt size={36} aria-label="Shopping List" />}
                        open={groceryOpen}
                        onClick={() => setGroceryOpen(open => !open)}
                        id="grocery-list"
                        labelId="grocery-list-label"
                    >
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
                            <h3 className="font-bold text-xl mb-4 text-blue-800">Your Shopping List:</h3>
                            <ul className="space-y-3">
                                {/* Show first 5 items normally */}
                                {plan.grocery_list.slice(0, 5).map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <span className="text-blue-600 mt-1 text-xl">•</span>
                                        <span className="text-lg text-gray-700">{formatGroceryItem(item)}</span>
                                    </li>
                                ))}

                                {/* Blurred block for the rest */}
                                {plan.grocery_list.length > 5 && (
                                    <li className="relative p-0 m-0 list-none">
                                        <ul className="space-y-3 filter blur-sm opacity-60 pointer-events-none select-none m-0">
                                            {plan.grocery_list.slice(5).map((item, idx) => (
                                                <li key={idx + 5} className="flex items-start gap-3">
                                                    <span className="text-blue-600 mt-1 text-xl">•</span>
                                                    <span className="text-lg text-gray-700">{formatGroceryItem(item)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        {/* Overlay shown once for all blurred items */}
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded pointer-events-auto">
                                            <button
                                                onClick={scrollToPricing}
                                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-green-400 to-blue-500 text-white font-bold text-lg shadow-lg hover:from-green-500 hover:to-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                                                aria-label="Start Free Trial to unlock and scroll to pricing"
                                            >
                                                Start Free Trial
                                            </button>
                                        </div>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </CollapsibleSection>
                )}


                {/* Personal Recommendations */}
                {plan.recommendations_user && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mt-6">
                        <h3 className="font-bold text-xl mb-4 text-green-800">Personal Notes for You:</h3>
                        <div className="whitespace-pre-line text-lg text-gray-700 leading-relaxed">
                            {plan.recommendations_user}
                        </div>
                    </div>
                )}


                {/* Disclaimer */}
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mt-8" role="alert">

                    <h3 className="font-bold text-xl mb-4 text-red-800">Important Health Notice:</h3>
                    <p className="text-lg text-red-700 leading-relaxed">
                        This site does not offer medical advice. Always prioritize your health and safety, and consult a healthcare professional with any health concerns. This program has not been evaluated by the FDA and is not intended to diagnose, treat, cure, or prevent any disease.
                    </p>
                </div>
            </main>

            <footer className="text-center mt-12 pb-8 px-4 mb-10">
                <p className="text-lg text-green-700 mb-2">Made with care for your health and wellness ❤️</p>
                <p className="text-gray-600">
                    <strong>Need Help?</strong> contact us at{" "}
                    <a href="mailto:support@oncodiary.com" className="text-green-600 hover:text-green-700 font-bold">
                        support@oncodiary.com
                    </a>
                </p>

                {showBottomBar && (

                    <div className="fixed left-0 right-0 bottom-0 bg-white/90 border-t border-gray-200 pointer-events-auto flex items-center justify-between px-4 py-3 z-50 shadow-lg">
                        <div className=" max-w-2xl mx-auto">
                            <span className="text-gray-700 font-medium text-base">
                                Start your free trial & <b>save 33%</b>!
                            </span>
                            <button
                                onClick={scrollToPricing}
                                className="ml-4 px-5 py-2 rounded-lg bg-gradient-to-r from-green-400 to-blue-500 text-white font-bold text-base shadow-md hover:from-green-500 hover:to-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-400"
                                aria-label="Start free trial and scroll to pricing"
                            >
                                Start Free Trial
                            </button>
                        </div>
                    </div>
                )}
                <div className="mt-4 space-x-4">
                    <Link to="/terms" className="underline hover:text-[#4B8F5A] transition">Terms of Use</Link>
                    <span className="text-gray-400">|</span>
                    <Link to="/privacy" className="underline hover:text-[#4B8F5A] transition">Privacy Policy</Link>
                </div>

            </footer>
        </div >
    );
}
